<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="fixed inset-0 overflow-hidden" @close="close()">
            <div class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0 bg-gray-500 opacity-75"/>

                <div class="fixed inset-y-0 pl-16 max-w-full right-0 flex">
                    <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700"
                                     enter-from="translate-x-full" enter-to="translate-x-0"
                                     leave="transform transition ease-in-out duration-500 sm:duration-700"
                                     leave-from="translate-x-0" leave-to="translate-x-full">
                        <div class="w-screen max-w-lg">
                            <form class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl"
                                  @submit.prevent="submit">
                                <div class="flex-1 h-0 overflow-y-auto">
                                    <div class="py-6 px-4 bg-amaze-coral sm:px-6">
                                        <div class="flex items-center justify-between">
                                            <DialogTitle class="text-lg text-white font-bold">
                                                Import Amaze Quote
                                            </DialogTitle>
                                            <div class="ml-3 h-7 flex items-center">
                                                <button type="button"
                                                        class="bg-amaze-coral rounded-md text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                        @click="close()">
                                                    <span class="sr-only">Close panel</span>
                                                    <XIcon class="h-6 w-6" aria-hidden="true"/>
                                                </button>
                                            </div>
                                        </div>
                                        <div class="mt-1">
                                            <p class="text-sm text-white">
                                                Select an Amaze quote to import.
                                            </p>
                                        </div>
                                    </div>
                                    <SlideOutErrors :show="showErrors === true"/>
                                    <div class="flex-1 flex flex-col justify-between">
                                        <div class="px-4 divide-y divide-gray-200 sm:px-6">
                                            <div class="space-y-6 pt-6 pb-5">
                                                <div>
                                                    <div class="mt-1">
                                                        <label
                                                            class="w-full mb-4 flex flex-col items-center px-4 py-6 bg-white text-blue rounded-lg tracking-wide uppercase border border-blue cursor-pointer">
                                                            <svg class="w-8 h-8" fill="currentColor"
                                                                 xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                                                <path
                                                                    d="M16.88 9.1A4 4 0 0 1 16 17H5a5 5 0 0 1-1-9.9V7a3 3 0 0 1 4.52-2.59A4.98 4.98 0 0 1 17 8c0 .38-.04.74-.12 1.1zM11 11h3l-4-4-4 4h3v3h2v-3z"/>
                                                            </svg>
                                                            <span v-if="form.file_uploads.length  === 0"
                                                                  class="mt-2 text-base leading-normal">Select a file</span>
                                                            <span v-else>{{ form.file_uploads.length }} file<span
                                                                v-if="form.file_uploads.length > 1">s</span> selected for upload</span>
                                                            <input type='file' class="hidden" multiple
                                                                   @change="prepareFileUpload($event)"/>
                                                        </label>
                                                    </div>
                                                </div>
                                                <div class="col-span-1">
                                                    <label for="folder-name"
                                                           class="block text-sm text-gray-900 required">
                                                        Country
                                                    </label>
                                                    <select id="country_id" v-model="form.country_id"
                                                            :class="{'border-red-500': form.errors.country_id}"
                                                            class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                        <option value selected v-html="'Select a country'"></option>
                                                        <option v-for="country in countries" :value="country.id">
                                                            {{ country.name }}
                                                        </option>
                                                    </select>
                                                </div>

                                                <div class="col-span-1">
                                                    <label for="folder-name"
                                                           class="block text-sm text-gray-900">
                                                        Region
                                                    </label>
                                                    <select :disabled="regions.length === 0" id="project_type_id"
                                                            v-model="form.region_id"
                                                            :class="{'border-red-500': form.errors.region_id}"
                                                            class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                        <option v-if="regions.length === 0" value selected>Select a
                                                            country
                                                        </option>
                                                        <option v-else value selected
                                                                v-html="'Select a region'"></option>
                                                        <option v-for="region in regions" :value="region.id"
                                                                v-html="region.name"></option>
                                                    </select>
                                                </div>

                                                <div class="col-span-1">
                                                    <label for="buy_vat_rate"
                                                           class="block text-sm text-gray-900 required">
                                                        VAT Percentage
                                                    </label>
                                                    <div class="mt-1">
                                                        <select id="buy_vat_rate"
                                                                v-model="form.vat_rate_id"
                                                                :class="{'border-red-500': form.errors.vat_rate_id}"
                                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                                            <option value selected
                                                                    v-html="'Select a VAT percentage'"></option>
                                                            <option v-for="vat_rate in vat_rates" :value="vat_rate.id">
                                                                {{ vat_rate.label }}
                                                            </option>

                                                        </select>
                                                    </div>
                                                </div>

                                                <div class="col-span-3">
                                                    <Combobox as="div" v-model="selectedSupplier" nullable>
                                                            <span class="block text-sm text-gray-900 flex items-center">
                                                                    <span class="required">Supplier</span>
                                                                    <a class="text-xs text-light-pitch-purple mx-3"
                                                                       target="_blank"
                                                                       :href="route('frontend.organisations.create', ['supplier'])">Create New Supplier</a>
                                                                    <RefreshIcon @click="refreshSuppliers()"
                                                                                 class="w-4 h-4 text-light-pitch-purple"></RefreshIcon>
                                                                </span>
                                                        <div class="relative mt-1">
                                                            <ComboboxInput
                                                                class="w-full mt-1 rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 shadow-sm focus:border-light-pitch-purple focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm"
                                                                @change="query = $event.target.value"
                                                                :display-value="(supplier) => supplier?.name"/>
                                                            <ComboboxButton
                                                                class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                                                                <SelectorIcon class="h-5 w-5 text-gray-400"
                                                                              aria-hidden="true"/>
                                                            </ComboboxButton>

                                                            <ComboboxOptions v-if="filteredSuppliers.length > 0"
                                                                             class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                                                <ComboboxOption
                                                                    v-for="supplier in filteredSuppliers"
                                                                    :key="supplier.id" :value="supplier"
                                                                    as="template" v-slot="{ active, selected }">
                                                                    <li :class="['relative cursor-pointer select-none py-2 pl-4 pr-4', active ? 'bg-amaze-coral text-white' : 'text-gray-900']">
                                                                    <span
                                                                        :class="['block truncate', selected && 'font-semibold']">
                                                                      {{ supplier.name }}
                                                                    </span>
                                                                    </li>
                                                                </ComboboxOption>
                                                            </ComboboxOptions>
                                                        </div>
                                                    </Combobox>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="flex-shrink-0 px-4 py-4 flex justify-end">
                                    <button type="button"
                                            class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            @click="close()">
                                        Close
                                    </button>
                                    <button :disabled="form.processing || !form.isDirty" type="submit"
                                            class=" ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-amaze-coral hover:bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-200">
                                        <span>Upload</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
<script>
import {ref, reactive, computed} from 'vue'
import {
    Combobox,
    ComboboxButton, ComboboxInput, ComboboxLabel, ComboboxOption, ComboboxOptions,
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot
} from '@headlessui/vue'
import {XIcon} from '@heroicons/vue/outline'
import {
    LinkIcon,
    PlusSmIcon,
    QuestionMarkCircleIcon,
    ExclamationCircleIcon,
    ArrowCircleRightIcon,
    RefreshIcon,
    SelectorIcon
} from '@heroicons/vue/solid'
import {useForm, usePage} from '@inertiajs/vue3'
import Inertia from '@inertiajs/vue3'
import SlideOutErrors from "@/Components/SlideOutErrors.vue";
import {mapState, mapActions} from 'vuex'
import {emitter} from "@/eventHub";


export default {
    components: {
        SlideOutErrors,
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        LinkIcon,
        PlusSmIcon,
        QuestionMarkCircleIcon,
        ExclamationCircleIcon,
        XIcon,
        ArrowCircleRightIcon,
        RefreshIcon,
        Combobox,
        ComboboxButton,
        ComboboxInput,
        ComboboxLabel,
        ComboboxOption,
        ComboboxOptions,
        SelectorIcon
    },
    data() {
        return {
            show: ref(false),
            showErrors: ref(false),
            query: '',
            selectedSupplier: ref(""),
            vat_rate: [],
            regions: [],
            form: useForm({
                file_uploads: [],
                quote_id: null,
                country_id: "",
                region_id: "",
                vat_rate_id: "",
                organisation_id: ref(),
            }),
        }
    },
    props: {
        quote: Object,
        project: Object
    },
    mounted() {
        emitter.on('openImportAmazeQuote', () => {
            this.open();
            this.form.country_id = this.project.country_id;
            this.form.region_id = this.project.region_id;
        });
    },
    methods: {
        submit() {

            this.form.post(route('frontend.project.quote.import.amaze', [this.project.id, this.quote.id]), {
                onSuccess: page => {
                    this.show = false;
                    this.showErrors = false;
                    this.form.reset();
                },
                onError: errors => {
                    this.showErrors = true;
                }
            });
        },
        close() {
            this.form.reset();
            this.form.clearErrors();
            this.show = false;
        },

        open() {
            this.form.clearErrors();
            this.show = true;
        },
        prepareFileUpload(event) {
            this.form.file_uploads = event.target.files
        },
        refreshSuppliers() {
            this.$store.dispatch('services/getAll', {'project_id': this.project.id, 'quote_id': this.quote.id});
        },
    },
    watch: {
        show: function (value) {
            if (value === true) {
                this.$store.dispatch('services/getAll', {
                    project_id: this.project.id,
                    quote_id: this.quote.id
                });

                this.regions = this.project.country.regions ?? [];
                this.vat_rates = this.project.country.vat_rates ?? [];
            }
        },
        selectedSupplier: {
            deep: true,
            handler(supplier) {
                this.form.organisation_id = supplier.id
            }
        },
    },
    computed: {
        ...mapState({
            suppliers: state => state.services.suppliers ?? null,
            countries: state => state.services.countries ?? null,
        }),
        filteredSuppliers: function () {
            return this.query === '' && this.query.length > 2
                ? this.suppliers
                : this.suppliers.filter((supplier) => {
                    return supplier.name.toLowerCase().includes(this.query.toLowerCase());
                })
        },
    }
}
</script>
